import React, { MouseEvent, useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";

// material-ui
import { useTheme } from "@mui/material/styles";
import {
  Box,
  Button,
  Stack,
  Tooltip,
  Typography,
  SnackbarProps,
  Chip,
} from "@mui/material";
import _debounce from "lodash/debounce";
// third-party

import { ColumnDef } from "@tanstack/react-table";
import { useQueryClient } from "@tanstack/react-query";

// project-import

import IconButton from "../../../../../components/@extended/IconButton";
import { FormattedMessage } from "react-intl";

// assets
import {
  DeleteOutlined,
  EditOutlined,
  FilterOutlined,
} from "@ant-design/icons";

import { openSnackbar } from "../../../../../api/snackbar";

import Loader from "../../../../../components/Loader";

import FiltersModal from "./FiltersModal";
import { USERAXIOS } from "../../../../../api/userAxios";
import ReusableTable from "../../../../../components/ReusableComponent/ReusableTable";
import ReusableDeleteDialog from "../../../../../components/ReusableComponent/ReusableDeleteDialog";
import { JobSeeker } from "../../../../../types/resumes-managment/jobSeeker";
import { JobSeekerFiltersType } from "./FiltersModal";

const JobSeekers = () => {
  const theme = useTheme();
  const location = useLocation();
  const queryClient = useQueryClient();
  const [openFilters, setOpenFilters] = useState(false);
  const [open, setOpen] = useState<boolean>(false);
  const [loading, setLoading] = useState(false);
  const [jobSeekerDeleteId, setJobSeekerDeleteId] = useState<any>("");
  const [result, setResult] = useState<JobSeeker[]>([]);
  const [currentPage, setCurrenttPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchText, setSearchText] = useState("");
  const [tempSearchText, setTempSearchText] = useState("");
  const [loadingSearch, setLoadingSearch] = useState(false);
  const [countPage, setCountPage] = useState({
    current_page: 0,
    last_page: 0,
  });
  const [isDeleting, setIsDeleting] = useState(false);

  const handleClose = () => {
    setOpen(!open);
  };

  const handleSearch = () => {
    setLoadingSearch(true);
    setCurrenttPage(1);
    setSearchText(tempSearchText);
    setLoadingSearch(false);
  };

  const columns = useMemo<ColumnDef<JobSeeker>[]>(
    () => [
      {
        header: () => <FormattedMessage id="ID" />,
        accessorKey: "id",
        meta: {
          className: "cell-center",
        },
        cell: ({ row }) => (
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Stack spacing={0}>
              <Typography variant="subtitle2">{row?.original.id}</Typography>
            </Stack>
          </Stack>
        ),
      },
      {
        header: () => <FormattedMessage id="User Name" />,
        accessorKey: "user_name",
        meta: {
          className: "cell-center",
        },
        cell: ({ row }) => (
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Stack spacing={0}>
              <Typography variant="subtitle2">
                {row?.original.user_name || "-"}
              </Typography>
            </Stack>
          </Stack>
        ),
      },

      {
        header: () => <FormattedMessage id="Full Name" />,
        accessorKey: "full_name",
        meta: {
          className: "cell-center",
        },
        cell: ({ row }) => (
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Stack spacing={0}>
              <Typography variant="subtitle2">
                {row?.original.full_name || "-"}
              </Typography>
            </Stack>
          </Stack>
        ),
      },

      {
        header: () => <FormattedMessage id="Title" />,
        accessorKey: "title_name",
        meta: {
          className: "cell-center",
        },
        cell: ({ row }) => (
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Stack spacing={0}>
              <Typography variant="subtitle2">
                {row?.original.title_name || "-"}
              </Typography>
            </Stack>
          </Stack>
        ),
      },

      {
        header: () => <FormattedMessage id="resume_id" />,
        accessorKey: "resume_id",
        meta: {
          className: "cell-center",
        },
        cell: ({ row }) => (
          <Stack spacing={0}>
            <Typography variant="subtitle2">
              {row?.original.resume_id || "-"}
            </Typography>
          </Stack>
        ),
      },

      {
        header: () => <FormattedMessage id="signup_lang" />,
        accessorKey: "signup_lang",
        meta: {
          className: "cell-center",
        },
        cell: ({ row }) => (
          <Stack spacing={0}>
            <Typography variant="subtitle2">
              {row?.original.signup_lang || "-"}
            </Typography>
          </Stack>
        ),
      },

      {
        header: () => <FormattedMessage id="created at" />,
        accessorKey: "created_at",
        cell: ({ row }) => (
          <Stack spacing={0}>
            <Typography variant="subtitle2">
              {row?.original.created_at || "-"}
            </Typography>
            <Typography color="text.secondary"></Typography>
          </Stack>
        ),
      },

      {
        header: () => <FormattedMessage id="Status" />,
        accessorKey: "status",
        meta: {
          className: "cell-center",
        },
        cell: (cell) => {
          const status = cell.getValue();
          return (
            <Chip
              color={status ? "success" : "error"}
              label={
                status ? (
                  <FormattedMessage id="Active" />
                ) : (
                  <FormattedMessage id="Inactive" />
                )
              }
              size="small"
              variant="filled"
            />
          );
        },
      },

      {
        header: () => <FormattedMessage id="Actions" />,
        accessorKey: "actions",
        meta: {
          className: "cell-center",
        },
        disableSortBy: true,
        cell: ({ row }) => {
          const { id } = row.original;

          const handleEditJobSeeker = () => {
            window.location.href = `/jobseekers/edit-jobseeker/${id}`;
          };

          return (
            <Stack direction="row" spacing={0}>
              <Tooltip title="Edit Profile">
                <IconButton
                  color="primary"
                  sx={{
                    "&:hover": {
                      backgroundColor: "transparent",
                    },
                  }}
                  onClick={handleEditJobSeeker}
                >
                  <EditOutlined />
                </IconButton>
              </Tooltip>
              <Tooltip title="Delete">
                <IconButton
                  color="error"
                  onClick={(e: MouseEvent<HTMLButtonElement>) => {
                    e.stopPropagation();
                    handleClose();
                    setJobSeekerDeleteId(row.original.id);
                  }}
                >
                  <DeleteOutlined />
                </IconButton>
              </Tooltip>
            </Stack>
          );
        },
      },
    ],
    // eslint-disable-next-line
    [theme]
  );

  const fetchData = () => {
    setLoading(true);
    const urlParams = new URLSearchParams(window.location.search);

    const full_name = urlParams.get("full_name");
    const full_name_arabic = urlParams.get("full_name_arabic");
    const user_name = urlParams.get("user_name");
    const resume_id = urlParams.get("resume_id");

    USERAXIOS.get(
      `/jobSeekers?page=${currentPage}` +
        (searchText ? `&title=${searchText}` : "") +
        (full_name ? `&full_name=${full_name}` : "") +
        (full_name_arabic ? `&full_name_arabic=${full_name_arabic}` : "") +
        (user_name ? `&user_name=${user_name}` : "") +
        (resume_id ? `&resume_id=${resume_id}` : "")
    )
      .then((response) => {
        const { data, meta } = response.data;
        const fetchedProfiles = data;
        const rowsWithKeys = fetchedProfiles.map((profile) => ({
          ...profile,
          key: profile.id,
        }));
        setResult(rowsWithKeys);
        setLoading(false);
        setCountPage(meta);
      })
      .catch((error) => {
        setLoading(false);
        console.error("Error fetching job seeker profiles:", error);
        openSnackbar({
          open: true,
          message: "Error fetching job seeker profiles",
          anchorOrigin: { vertical: "top", horizontal: "right" },
          variant: "alert",
          alert: {
            color: "error",
          },
        } as SnackbarProps);
      });
  };

  useEffect(() => {
    fetchData();
  }, [currentPage, searchText]);

  useEffect(() => {
    fetchData();
  }, [location.search]);

  const handlePageChange = (
    event: React.ChangeEvent<unknown>,
    page: number
  ) => {
    setCurrenttPage(page);
  };

  const handleFiltersClose = () => {
    setOpenFilters(false);
  };

  const clearFilters = () => {
    setLoading(true);
    window.history.pushState({}, "", `${window.location.pathname}`);
    USERAXIOS.get(
      `/jobSeekers?page=${currentPage}` +
        (searchText ? `&title=${searchText}` : "")
    )
      .then((response) => {
        const { data, meta } = response.data;
        const fetchedProfiles = data;
        const rowsWithKeys = fetchedProfiles.map((profile) => ({
          ...profile,
          key: profile.id,
        }));
        setResult(rowsWithKeys);
        setLoading(false);
        setCountPage(meta);
      })
      .catch((error) => {
        setLoading(false);
        console.error("Error fetching job seeker profiles:", error);
        openSnackbar({
          open: true,
          message: "Error fetching job seeker profiles",
          anchorOrigin: { vertical: "top", horizontal: "right" },
          variant: "alert",
          alert: {
            color: "error",
          },
        } as SnackbarProps);
      });
  };

  const hasActiveFilters = () => {
    const urlParams = new URLSearchParams(window.location.search);
    return !!(
      urlParams.get("full_name") ||
      urlParams.get("full_name_arabic") ||
      urlParams.get("user_name") ||
      urlParams.get("resume_id")
    );
  };

  const handleFiltersApply = (filters: JobSeekerFiltersType) => {
    setLoading(true);
    const { full_name, full_name_arabic, user_name, resume_id } = filters;
    const urlParams = new URLSearchParams(window.location.search);
    urlParams.delete("full_name");
    urlParams.delete("full_name_arabic");
    urlParams.delete("user_name");
    urlParams.delete("resume_id");
    if (full_name) urlParams.set("full_name", full_name);
    if (full_name_arabic) urlParams.set("full_name_arabic", full_name_arabic);
    if (user_name) urlParams.set("user_name", user_name);
    if (resume_id) urlParams.set("resume_id", resume_id);
    const newUrl = `${window.location.pathname}?${urlParams.toString()}`;
    window.history.pushState({}, "", newUrl);
    USERAXIOS.get(
      `/jobSeekers?page=${currentPage}` +
        (full_name ? `&full_name=${full_name}` : "") +
        (full_name_arabic ? `&full_name_arabic=${full_name_arabic}` : "") +
        (user_name ? `&user_name=${user_name}` : "") +
        (resume_id ? `&resume_id=${resume_id}` : "") +
        (searchText ? `&title=${searchText}` : "")
    )
      .then((response) => {
        const { data, meta } = response.data;
        const fetchedProfiles = data;
        const rowsWithKeys = fetchedProfiles.map((profile) => ({
          ...profile,
          key: profile.id,
        }));
        setResult(rowsWithKeys);
        setLoading(false);
        setCountPage(meta);
      })
      .catch((error) => {
        setLoading(false);
        console.error("Error fetching job seeker profiles:", error);
        openSnackbar({
          open: true,
          message: "Error fetching job seeker profiles",
          anchorOrigin: { vertical: "top", horizontal: "right" },
          variant: "alert",
          alert: {
            color: "error",
          },
        } as SnackbarProps);
      });
  };

  const handleDelete = async () => {
    try {
      setIsDeleting(true);
      await USERAXIOS.delete(`/jobSeekers/${jobSeekerDeleteId}`);
      setResult((prevResults) =>
        prevResults.filter((item) => item.id !== jobSeekerDeleteId)
      );
      queryClient.invalidateQueries({ queryKey: ["jobSeekers"] });
      handleClose();
      openSnackbar({
        open: true,
        message: "Job seeker profile deleted successfully",
        anchorOrigin: { vertical: "top", horizontal: "right" },
        variant: "alert",
        alert: {
          color: "success",
        },
      } as SnackbarProps);
    } catch (error) {
      console.error("Error deleting job seeker profile:", error);
      openSnackbar({
        open: true,
        message: "Failed to delete job seeker profile",
        anchorOrigin: { vertical: "top", horizontal: "right" },
        variant: "alert",
        alert: {
          color: "error",
        },
      } as SnackbarProps);
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <>
      {loading ? (
        <Loader />
      ) : (
        <>
          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2 }}>
            {hasActiveFilters() && (
              <Button
                variant="outlined"
                color="primary"
                onClick={clearFilters}
                sx={{ mb: 2, padding: ".3rem 2rem " }}
              >
                <FormattedMessage id="Clear Filters" />
              </Button>
            )}
            <Button
              variant="contained"
              startIcon={<FilterOutlined />}
              onClick={() => setOpenFilters(true)}
              sx={{
                mb: 2,
                padding: ".3rem 2rem",
                gap: ".5rem",
              }}
            >
              <FormattedMessage id="Filters" />
            </Button>
          </Box>
          <ReusableTable
            data={result}
            columns={columns}
            countPage={countPage}
            handlePageChange={handlePageChange}
            handleSearchChange={handleSearch}
            currentPage={currentPage}
            pageSize={pageSize}
            setPageSize={setPageSize}
            setCurrenttPage={setCurrenttPage}
            loadingSearsh={loadingSearch}
            searchedtext={tempSearchText}
            setSearchedtext={setTempSearchText}
            modalToggler={() => {
              window.location.href = `/jobseekers/add-jobseeker`;
            }}
            renderDetailPanel={({ row }: any) => <></>}
          />
        </>
      )}

      {openFilters && (
        <FiltersModal
          open={openFilters}
          handleClose={handleFiltersClose}
          onApplyFilters={handleFiltersApply}
          onClearFilters={clearFilters}
        />
      )}

      <ReusableDeleteDialog
        open={open}
        handleClose={handleClose}
        handleDelete={handleDelete}
        isDeleting={isDeleting}
      />
    </>
  );
};

export default JobSeekers;
